import React, { FunctionComponent, useContext, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShoppingPlannerContext } from '../../Context/ShoppingPlannerContext';
import { useProductsFromStates } from '../../Hook/UseProductsFromStates';
import { useShoppingList } from '../../Hook/UseShoppingList';
import { Icon } from '../../Component/Icon/Icon';
import { BarCode } from './ShoppingList';
import { centsToEuroString } from '../../Component/Formatter/Currency';
import { urls } from '../../Constants/Urls';

import './ShoppingListMobile.scss';

interface ShoppingListMobileProps {}

export const ShoppingListMobile: FunctionComponent<ShoppingListMobileProps> = (props) => {
    const { sendUserInputToServer, slug, setSlug, selectedInstallationAndCare } = useContext(ShoppingPlannerContext);

    const { floor, decor, insulation, skirtingDecor, skirting, installationAndCare, sqm } = useProductsFromStates();

    const { slugFromUrl } = useParams<{ slugFromUrl?: string }>();

    const {
        decorPackageAmount,
        decorPrice,
        skirtingPackageAmount,
        skirtingPrice,
        insulationPackageAmount,
        insulationPrice,
        cornersAndCaps,
    } = useShoppingList();

    useEffect(() => {
        if (slugFromUrl) {
            setSlug(slugFromUrl);
        } else if (!slug) {
            sendUserInputToServer();
        }
    }, []);

    return (
        <div className="shopping-planner-shopping-list-mobile">
            <div className="shopping-planner-shopping-list-mobile__selection">
                <h4 className="shopping-planner-shopping-list-mobile__headline">Ihre Auswahl:</h4>
                {sqm ? (
                    <div className="shopping-planner-shopping-list-mobile__room-size">
                        <div>
                            <span className="shopping-planner-shopping-list-mobile__label">{sqm}m²</span>
                            <span className="shopping-planner-shopping-list-mobile__sub-label">Raumgröße</span>
                        </div>
                        <Link to={urls.steps.room.builder()} className="shopping-planner-shopping-list-mobile__link">
                            <Icon icon="edit" />
                        </Link>
                    </div>
                ) : null}
                {floor && decor ? (
                    <Link to={urls.steps.room.builder()} className="shopping-planner-shopping-list-mobile__item">
                        <img
                            className="shopping-planner-shopping-list-mobile__selection-image"
                            src={decor.imageUri ?? ''}
                        />
                        <div>
                            <span
                                className={[
                                    'shopping-planner-shopping-list-mobile__label',
                                    'shopping-planner-shopping-list-mobile__label--uppercase',
                                    'floor-colour-' + floor.colorId,
                                ].join(' ')}
                            >
                                {floor.floorFamilyLabel}
                            </span>
                            <span className="shopping-planner-shopping-list-mobile__sub-label">{decor.label}</span>
                        </div>

                        <div className="shopping-planner-shopping-list-mobile__price">
                            {decorPackageAmount} Pakete à
                            {Intl.NumberFormat('de-DE').format(decor.squareMetersPerPackage)} m² <br />
                            <strong>{centsToEuroString(decorPrice)}</strong>
                        </div>
                        <div className="shopping-planner-shopping-list-mobile__bar-code-container">
                            <BarCode ean={decor.ean} />
                        </div>
                    </Link>
                ) : null}
                {insulation ? (
                    <Link
                        to={urls.steps.accessoriesInsulation.builder()}
                        className="shopping-planner-shopping-list-mobile__item"
                    >
                        <img
                            className="shopping-planner-shopping-list-mobile__selection-image"
                            src={insulation.imageUri ?? ''}
                        />
                        <div>
                            <span
                                className={[
                                    'shopping-planner-shopping-list-mobile__label',
                                    'shopping-planner-shopping-list-mobile__label--uppercase',
                                ].join(' ')}
                            >
                                {insulation.label}
                            </span>
                            <span className="shopping-planner-shopping-list-mobile__sub-label">Dämmung</span>
                        </div>

                        <div className="shopping-planner-shopping-list-mobile__price">
                            <div>
                                {insulationPackageAmount} Pakete à{' '}
                                {Intl.NumberFormat('de-DE').format(insulation.squareMetersPerPackage)} m²
                            </div>
                            <strong>{centsToEuroString(insulationPrice)}</strong>
                        </div>
                        <div className="shopping-planner-shopping-list-mobile__bar-code-container">
                            <BarCode ean={insulation.ean} />
                        </div>
                    </Link>
                ) : null}
                {skirtingDecor ? (
                    <Link
                        to={urls.steps.accessoriesSkirting.builder()}
                        className="shopping-planner-shopping-list-mobile__item"
                    >
                        <img
                            className="shopping-planner-shopping-list-mobile__selection-image"
                            src={skirtingDecor.imageUri ?? ''}
                        />
                        <div>
                            <span
                                className={[
                                    'shopping-planner-shopping-list-mobile__label',
                                    'shopping-planner-shopping-list-mobile__label--uppercase',
                                ].join(' ')}
                            >
                                {skirting?.label}
                            </span>
                            <span className="shopping-planner-shopping-list-mobile__sub-label">Leisten</span>
                        </div>

                        <div className="shopping-planner-shopping-list-mobile__price">
                            {skirting && skirtingPackageAmount ? (
                                <>
                                    {skirtingPackageAmount} Leisten à{' '}
                                    {Intl.NumberFormat('de-DE').format(skirting?.dimensions.length / 1000)} m
                                </>
                            ) : null}
                            <div>{cornersAndCaps.join(', ')}</div>
                            <strong>{skirtingPrice > 0 ? centsToEuroString(skirtingPrice) : null}</strong>
                        </div>

                        <div className="shopping-planner-shopping-list-mobile__bar-code-container">
                            <BarCode ean={skirtingDecor.ean} />
                        </div>
                    </Link>
                ) : null}
                {installationAndCare.length > 0
                    ? installationAndCare.map((installation) => (
                          <Link
                              to={urls.steps.accessoriesInstallation.builder()}
                              key={installation.accessoryId}
                              className="shopping-planner-shopping-list-mobile__item"
                          >
                              <img
                                  className="shopping-planner-shopping-list-mobile__selection-image"
                                  src={installation.imageUri ?? ''}
                              />
                              <div>
                                  <span
                                      className={[
                                          'shopping-planner-shopping-list-mobile__label',
                                          'shopping-planner-shopping-list-mobile__label--uppercase',
                                      ].join(' ')}
                                  >
                                      {installation.label}
                                  </span>
                                  <span className="shopping-planner-shopping-list-mobile__sub-label">
                                      Verlegung & Pflege
                                  </span>
                              </div>
                              <div className="shopping-planner-shopping-list-mobile__price">
                                  <strong>
                                      {centsToEuroString(
                                          installation.variants
                                              .filter((variant) =>
                                                  selectedInstallationAndCare.includes(variant.articleNumber)
                                              )
                                              .reduce((acc, variant) => variant.pricePerPiece + acc, 0)
                                      )}
                                  </strong>
                              </div>
                              <div className="shopping-planner-shopping-list-mobile__bar-code-container">
                                  {installation.variants
                                      .filter((variant) => selectedInstallationAndCare.includes(variant.articleNumber))
                                      .map((variant) => (
                                          <BarCode key={variant.ean} ean={variant.ean} />
                                      ))}
                              </div>
                          </Link>
                      ))
                    : null}
            </div>
        </div>
    );
};
